import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import translations, { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from './translations';

const STORAGE_KEY = 'loysta-marketing-lang';

const LanguageContext = createContext({
  lang: DEFAULT_LANGUAGE,
  t: translations[DEFAULT_LANGUAGE],
  setLang: () => {},
});

const isSupported = (code) => SUPPORTED_LANGUAGES.some((l) => l.code === code);

const readInitialLang = () => {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && isSupported(saved)) return saved;
  } catch (_) { /* ignore */ }
  return DEFAULT_LANGUAGE;
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(readInitialLang);

  const setLang = (next) => {
    if (!isSupported(next)) return;
    setLangState(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch (_) { /* ignore */ }
  };

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', lang);
    }
  }, [lang]);

  const value = useMemo(() => ({
    lang,
    t: translations[lang] || translations[DEFAULT_LANGUAGE],
    setLang,
  }), [lang]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
